import React, { useEffect, useState } from 'react';
import { Grid, Card, CardContent, Typography, Box, IconButton, CardActions, CircularProgress, CardMedia, Chip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import ProductImagesService from '../../../services/ProductImagesService';
import { formatMoney } from '../../../utils/currency';

const STOCK_TRACKED_TYPES = new Set(['DEFINED', 'RETAIL']);

const formatStockCount = (stock) =>
  Number.isInteger(stock) ? stock.toLocaleString('de-DE') : stock.toLocaleString('de-DE', { maximumFractionDigits: 2 });

// Naranja si está por debajo del stock mínimo, rojo si no queda stock.
const getStockBadge = (product) => {
  if (!STOCK_TRACKED_TYPES.has(product.productType)) return null;

  const stock = Number(product.stock);
  const minimumStock = Number(product.minimumStock);
  if (!Number.isFinite(stock) || !Number.isFinite(minimumStock) || stock >= minimumStock) return null;

  if (stock <= 0) {
    return { label: 'Sin stock', color: '#d32f2f' };
  }
  return { label: `${formatStockCount(stock)} en stock`, color: '#ed6c02' };
};

const ProductGrid = ({ products, onProductClick, onRemoveProduct, categoryViewId, locationId, showDeleteButtons = false, deletingProductIds = new Set() }) => {
  const [productImages, setProductImages] = useState({});
  const isDeleting = (productId) => deletingProductIds && (deletingProductIds.has?.(productId) ?? deletingProductIds.includes?.(productId));

  useEffect(() => {
    let mounted = true;

    const loadImages = async () => {
      try {
        const imageMap = await ProductImagesService.getPrimaryImageMap(products.map((product) => product.id));
        if (!mounted) return;

        setProductImages((prev) => ({
          ...prev,
          ...imageMap,
        }));
      } catch (error) {
        if (!mounted) return;
      }
    };

    loadImages();

    return () => {
      mounted = false;
    };
  }, [products]);

  return (
    <Grid container spacing={2}>
      {products.map((product) => {
        const deleting = isDeleting(product.id);
        const imageUrl = productImages[product.id];
        const stockBadge = getStockBadge(product);
        return (
          <Grid item xs={6} sm={4} md={3} key={product.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', opacity: deleting ? 0.7 : 1, position: 'relative' }}>
              {stockBadge && (
                <Chip
                  label={stockBadge.label}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 6,
                    left: 6,
                    zIndex: 2,
                    height: 20,
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#fff',
                    backgroundColor: stockBadge.color,
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              )}
              {deleting && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'action.hover',
                    zIndex: 1,
                    borderRadius: 1,
                  }}
                >
                  <CircularProgress size={32} />
                </Box>
              )}
              <Box
                onClick={() => !deleting && onProductClick(product)}
                sx={{
                  cursor: deleting ? 'wait' : 'pointer',
                  aspectRatio: '1 / 1',
                  width: '100%',
                  backgroundColor: '#f4f1ea',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                {imageUrl ? (
                  <CardMedia
                    component="img"
                    image={imageUrl}
                    alt={product.alias || product.name}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'linear-gradient(135deg, #f7f3eb 0%, #ebe3d3 100%)',
                      color: '#9c8f7a',
                    }}
                  >
                    <ImageNotSupportedOutlinedIcon sx={{ fontSize: 40 }} />
                  </Box>
                )}
              </Box>
              <CardContent 
                onClick={() => !deleting && onProductClick(product)}
                sx={{
                  cursor: deleting ? 'wait' : 'pointer',
                  flex: 1,
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: 0.5,
                  py: 1.25,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    lineHeight: 1.25,
                    minHeight: 36,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {product.alias || product.name}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatMoney(product.priceLocal ?? product.price, product.priceCurrency)}
                </Typography>
              </CardContent>
              {showDeleteButtons && onRemoveProduct && categoryViewId && (
                <CardActions sx={{ justifyContent: 'center', pt: 0 }}>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={deleting}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!deleting) onRemoveProduct(product.id, categoryViewId);
                    }}
                    title={deleting ? 'Eliminando...' : 'Eliminar de esta categoría'}
                    sx={{
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: 'error.main',
                        '& svg': {
                          color: 'white',
                        }
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              )}
            </Card>
          </Grid>
        );
      })}

      {products.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '500px',
            width: '100%',
          }}
        >
          <Typography variant="body2">
            No hay productos para esta subcategoría.
          </Typography>
        </Box>
      )}

    </Grid>
  );
};

export default ProductGrid;
